"use client";

import { Box, Breadcrumbs, Container, Typography } from "@mui/material";
import { FC } from "react";
import SalifyFallback from "src/lib/components/salify-fallback/default";
import { SalifyLinkV2 } from "src/lib/components/ui/link/link";

export interface BreadcrumbItem {
  text?: string;
  href?: string;
  icon?: string; // Path to icon image or iconify icon name
}

export interface VariantProps {
  title: string;
  backgroundImage?: string;
  breadcrumbs: BreadcrumbItem[];
  overlayOpacity?: number;
  minHeight?: string;
}

const PageBanner: FC<VariantProps> = ({
  title,
  backgroundImage,
  breadcrumbs,
  overlayOpacity = 0.5,
  minHeight = "400px",
}) => {
  const safeTitle = title?.trim() || "Trang nội dung";
  return (
    <Box
      sx={{
        position: "relative",
        width: "100%",
        minHeight,
        backgroundImage: backgroundImage ? `url(${backgroundImage})` : undefined,
        backgroundSize: "cover",
        backgroundPosition: "center center",
        backgroundRepeat: "no-repeat",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        "&::before": {
          content: '""',
          position: "absolute",
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: `rgba(0, 0, 0, ${overlayOpacity})`,
          zIndex: 1,
        },
      }}
    >
      <Container
        maxWidth="lg"
        sx={{
          position: "relative",
          zIndex: 2,
          textAlign: "center",
          py: 6,
        }}
      >
        {/* Breadcrumbs */}
        <Breadcrumbs
          aria-label="breadcrumb"
          sx={{
            justifyContent: "center",
            mb: 3,
            "& .MuiBreadcrumbs-ol": {
              justifyContent: "center",
            },
            "& .MuiBreadcrumbs-separator": {
              color: "white",
            },
          }}
        >
          {breadcrumbs.map((breadcrumb, index) => {
            const isLast = index === breadcrumbs.length - 1;

            const renderBreadcrumbContent = () => {
              if (breadcrumb.icon) {
                return (
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 0.5,
                    }}
                  >
                    {breadcrumb.icon.startsWith("/") ? (
                      <Box
                        component="img"
                        src={breadcrumb.icon}
                        alt=""
                        sx={{
                          width: 16,
                          height: 16,
                          filter: "brightness(0) invert(1)",
                          opacity: 1,
                        }}
                      />
                    ) : null}
                    {breadcrumb.text && (
                      <SalifyFallback.Label
                        path={`breadcrumbs.${index}.text`}
                        default={breadcrumb.text}
                      >
                        {breadcrumb.text}
                      </SalifyFallback.Label>
                    )}
                  </Box>
                );
              }

              return (
                <SalifyFallback.Label
                  path={`breadcrumbs.${index}.text`}
                  default={breadcrumb.text || ""}
                >
                  {breadcrumb.text}
                </SalifyFallback.Label>
              );
            };

            if (isLast || !breadcrumb.href) {
              return (
                <Typography
                  key={index}
                  sx={{
                    color: isLast ? "#FFC107" : "white",
                    fontSize: "0.875rem",
                    fontWeight: isLast ? 600 : 400,
                    display: "flex",
                    alignItems: "center",
                  }}
                >
                  {renderBreadcrumbContent()}
                </Typography>
              );
            }

            return (
              <SalifyLinkV2
                key={index}
                href={breadcrumb.href}
                sx={{
                  color: "white",
                  textDecoration: "none",
                  fontSize: "0.875rem",
                  fontWeight: 400,
                  display: "flex",
                  alignItems: "center",
                  "&:hover": {
                    color: "white",
                    textDecoration: "underline",
                  },
                }}
              >
                {renderBreadcrumbContent()}
              </SalifyLinkV2>
            );
          })}
        </Breadcrumbs>

        {/* Page Title */}
        <Typography
          variant="h1"
          component="h1"
          sx={{
            color: "white",
            fontWeight: 700,
            fontSize: { xs: "2rem", sm: "2.5rem", md: "3rem" },
            letterSpacing: "0.02em",
            textShadow: "0 2px 8px rgba(0, 0, 0, 0.3)",
            lineHeight: 1.2,
          }}
        >
          <SalifyFallback.Label path="title" default={title}>
            {safeTitle}
          </SalifyFallback.Label>
        </Typography>
      </Container>
    </Box>
  );
};

PageBanner.displayName = "PageBanner";

export default PageBanner;
